// Bulk and custom order page: groomsmen parties, employee gifts, events,
// graduating groups. Primary CTA is a free mockup + quote form (submits to
// Supabase bulk_quotes; falls back to a prefilled email if that fails).
import React, { useState, useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { makeBreadcrumbJsonLd, makeFaqJsonLd } from "../seo/structuredData";
import boxesPhoto from "../assets/boxes1.webp";
import fullPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";
import closedPhoto from "../assets/newest-version/Full - nothing extended just basic.jpg";

const futura = "'Futura', sans-serif";

const BULK_FAQ = [
  {
    q: "What can be customized on a bulk Brik order?",
    a: "Names, initials, or your company logo on each wallet, plus individual gift boxes and name cards. Submit the quote form with your artwork and you'll get a visual mockup before anything goes into production.",
  },
  {
    q: "How long does a bulk custom order take?",
    a: "Plan on three to four weeks from artwork approval to delivery. Rush production may be available for a 15% fee depending on quantity and timing, so include your needed-by date in the quote form.",
  },
  {
    q: "How does payment work for bulk orders?",
    a: "Under 25 units: full payment after you approve the mockup. Over 25 units: a 50% deposit to start production and the balance before shipping. Every unit is inspected before it ships.",
  },
  {
    q: "Can you ship to individual addresses?",
    a: "Yes. One destination is standard; shipping to individual addresses is available at additional cost. Tell us which you need in the quote form.",
  },
  {
    q: "Are custom orders returnable?",
    a: "Personalized products are final sale except for defects. If anything arrives damaged or wrong, we make it right.",
  },
];

const PROCESS_STEPS = [
  { title: "Submit the form", text: "Two minutes. Occasion, quantity, and what you want on the wallets." },
  { title: "Get a mockup and quote", text: "A visual mockup and real pricing, not a 'thanks, we'll contact you.'" },
  { title: "Approve the artwork", text: "Nothing goes into production until you sign off on exactly how it looks." },
  { title: "Payment", text: "Full payment under 25 units; 50% deposit over 25, balance before shipping." },
  { title: "Production and inspection", text: "Engraving is produced, then we inspect every single unit ourselves." },
  { title: "Ship", text: "One destination or individual addresses. Then send us the group photo." },
];

const TERMS = [
  "Production begins after artwork approval and payment.",
  "Personalized products are final sale except for defects.",
  "Colors may vary slightly from mockups.",
  "You confirm you have permission to use any logo you submit.",
  "Delivery dates are estimates until production is confirmed.",
  "Shipping to individual addresses costs extra.",
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const labelStyle = {
  fontFamily: futura,
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.5px",
  color: "#111",
  display: "block",
  margin: "0 0 6px",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontFamily: futura,
  fontSize: "15px",
  background: "white",
  color: "#111",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  border: "none",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  cursor: "pointer",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const EMPTY_FORM = {
  name: "",
  email: "",
  company: "",
  occasion: "",
  quantity: "",
  neededBy: "",
  personalization: "",
  logoLink: "",
  shippingMode: "",
  zip: "",
  notes: "",
};

const BulkOrders = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState(""); // "", "sending", "sent", "error"
  const formRef = useRef(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const mailtoHref = () => {
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Company/organization: ${form.company}`,
      `Occasion: ${form.occasion}`,
      `Quantity: ${form.quantity}`,
      `Needed by: ${form.neededBy}`,
      `Personalization: ${form.personalization}`,
      `Logo link: ${form.logoLink}`,
      `Shipping: ${form.shippingMode}`,
      `ZIP: ${form.zip}`,
      `Notes: ${form.notes}`,
    ].join("\n");
    return `mailto:asray@the-brik.com?subject=${encodeURIComponent(
      "Bulk order quote request"
    )}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.quantity) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    if (typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", {
        currency: "USD",
        value: Number(form.quantity) * 69.99 || undefined,
      });
    }
    try {
      const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
      const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;
      const res = await fetch(`${SUPABASE_URL}/rest/v1/bulk_quotes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company: form.company,
          occasion: form.occasion,
          quantity: form.quantity,
          needed_by: form.neededBy,
          personalization: form.personalization,
          logo_link: form.logoLink,
          shipping_mode: form.shippingMode,
          zip: form.zip,
          notes: form.notes,
        }),
      });
      setStatus(res.ok ? "sent" : "fallback");
    } catch {
      setStatus("fallback");
    }
  };

  const faqJsonLd = makeFaqJsonLd(BULK_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Bulk & Custom Orders", path: "/bulk" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Bulk Custom Wallets: Names, Initials or Your Logo"
        description="Customize the Brik with names, initials, or your company logo. Built for groomsmen, employee gifts, events, and graduating groups. Get a free mockup and quote."
        path="/bulk"
        keywords="bulk custom wallets, engraved wallets bulk order, corporate gift wallets, employee gifts, custom groomsmen wallets, company logo wallet, group gift wallets"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            BULK & CUSTOM ORDERS
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(32px, 4.5vw, 54px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            The wallet people will actually keep.
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "640px",
              margin: "0 auto 26px",
            }}
          >
            Customize the Brik with names, initials or your company logo.
            Built for groomsmen, employee gifts, events and graduating groups.
          </p>
          <button type="button" onClick={scrollToForm} style={ctaButton}>
            Get a free mockup and quote
          </button>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            You get a visual mockup and real pricing, usually within a day.
          </p>
        </div>
      </section>

      {/* Visual strip */}
      <section style={{ background: "white", padding: "60px 24px 20px" }}>
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "16px",
          }}
        >
          {[
            {
              src: boxesPhoto,
              alt: "Multiple Brik wallets packed in individual gift boxes for a group order",
              label: "Boxed for the whole group",
            },
            {
              src: fullPhoto,
              alt: "The Brik wallet with cards, ID, cash, keys, and tracker installed",
              label: "Cards, keys, cash and tracker",
            },
            {
              src: closedPhoto,
              alt: "Black anodized aluminum Brik wallet ready for engraving with initials or a logo",
              label: "Your names, initials or logo",
            },
          ].map((img) => (
            <figure key={img.label} style={{ margin: 0 }}>
              <div
                style={{
                  background: "#989898",
                  borderRadius: "14px",
                  overflow: "hidden",
                  aspectRatio: "4 / 3",
                }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
              <figcaption
                style={{
                  fontFamily: futura,
                  fontSize: "13.5px",
                  fontWeight: 700,
                  color: "#555",
                  textAlign: "center",
                  padding: "10px 0 0",
                }}
              >
                {img.label}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Founder story */}
      <section style={{ background: "white", padding: "50px 24px 70px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={sectionHeading}>It started with a $160 lost dorm key.</h2>
          <p style={{ ...bodyText, maxWidth: "600px", margin: "0 auto" }}>
            After losing an expensive dorm key, Iowa State student Asray Gopa
            designed one place to keep his ID, cards, cash, keys and tracker
            together. More than 1,000 Briks have now been sold.{" "}
            <a href="/about" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Read the story
            </a>
            .
          </p>
        </div>
      </section>

      {/* Process */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            How a custom order works
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "18px",
            }}
          >
            {PROCESS_STEPS.map((s, i) => (
              <div
                key={s.title}
                style={{
                  background: "white",
                  borderRadius: "14px",
                  padding: "22px 22px 20px",
                  border: "1px solid #e6e6e6",
                }}
              >
                <div
                  style={{
                    fontFamily: futura,
                    width: "34px",
                    height: "34px",
                    borderRadius: "50%",
                    background: "#ff7d1a",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "15px",
                    marginBottom: "12px",
                  }}
                >
                  {i + 1}
                </div>
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "16.5px",
                    margin: "0 0 6px",
                    color: "#111",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {s.title}
                </h3>
                <p style={{ ...bodyText, fontSize: "14.5px", margin: 0 }}>{s.text}</p>
              </div>
            ))}
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              textAlign: "center",
              margin: "26px 0 0",
            }}
          >
            Typical lead time: three to four weeks from artwork approval. Rush
            production may be available for a 15% fee. No customization needed?{" "}
            <a href="/groomsmen-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Groomsmen packs
            </a>{" "}
            ship gift-ready in 5-7 days.
          </p>
        </div>
      </section>

      {/* Quote form */}
      <section ref={formRef} style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "680px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center" }}>
            Get a free mockup and quote
          </h2>
          <p style={{ ...bodyText, textAlign: "center", margin: "0 0 30px" }}>
            Fill this out and you'll get back a visual mockup and price.
          </p>

          {status === "sent" ? (
            <div
              style={{
                background: "#eef9f0",
                border: "1px solid #bfe6c8",
                borderRadius: "14px",
                padding: "28px",
                textAlign: "center",
                fontFamily: futura,
              }}
            >
              <p style={{ fontSize: "18px", fontWeight: 700, color: "#2e7d32", margin: "0 0 8px" }}>
                Got it. Mockup and quote coming your way.
              </p>
              <p style={{ fontSize: "14.5px", color: "#444", margin: 0 }}>
                We'll reply to {form.email}, usually within a day. If your
                logo file is ready, you can also send it directly to{" "}
                <a href="mailto:asray@the-brik.com" style={{ color: "#ff7d1a", fontWeight: 600 }}>
                  asray@the-brik.com
                </a>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "16px",
                }}
              >
                <div>
                  <label style={labelStyle} htmlFor="bq-name">Name *</label>
                  <input id="bq-name" style={inputStyle} value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-email">Email *</label>
                  <input id="bq-email" type="email" style={inputStyle} value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-company">Company or organization</label>
                  <input id="bq-company" style={inputStyle} value={form.company} onChange={set("company")} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-occasion">What is the occasion?</label>
                  <select id="bq-occasion" style={inputStyle} value={form.occasion} onChange={set("occasion")}>
                    <option value="">Select...</option>
                    <option>Groomsmen / wedding party</option>
                    <option>Employee or client gifts</option>
                    <option>Event or conference</option>
                    <option>Graduating group / team</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-qty">Desired quantity *</label>
                  <input id="bq-qty" type="number" min="2" style={inputStyle} value={form.quantity} onChange={set("quantity")} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-date">Needed-by date</label>
                  <input id="bq-date" type="date" style={inputStyle} value={form.neededBy} onChange={set("neededBy")} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-pers">Names, initials or logo?</label>
                  <select id="bq-pers" style={inputStyle} value={form.personalization} onChange={set("personalization")}>
                    <option value="">Select...</option>
                    <option>Individual names</option>
                    <option>Initials</option>
                    <option>Company logo</option>
                    <option>Mix / not sure yet</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-logo">Link to your logo (Drive, Dropbox...)</label>
                  <input id="bq-logo" style={inputStyle} placeholder="Optional; we can collect it by email" value={form.logoLink} onChange={set("logoLink")} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-ship">Shipping</label>
                  <select id="bq-ship" style={inputStyle} value={form.shippingMode} onChange={set("shippingMode")}>
                    <option value="">Select...</option>
                    <option>One destination</option>
                    <option>Individual addresses (costs extra)</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle} htmlFor="bq-zip">Shipping ZIP code</label>
                  <input id="bq-zip" style={inputStyle} value={form.zip} onChange={set("zip")} />
                </div>
              </div>
              <div style={{ marginTop: "16px" }}>
                <label style={labelStyle} htmlFor="bq-notes">Additional notes</label>
                <textarea id="bq-notes" rows={4} style={{ ...inputStyle, resize: "vertical" }} value={form.notes} onChange={set("notes")} />
              </div>
              <div style={{ textAlign: "center", marginTop: "24px" }}>
                <button type="submit" style={ctaButton} disabled={status === "sending"}>
                  {status === "sending" ? "Sending..." : "Get my free mockup and quote"}
                </button>
                {status === "error" && (
                  <p style={{ fontFamily: futura, color: "#b71c1c", fontWeight: 600, fontSize: "14px", margin: "12px 0 0" }}>
                    Name, email, and quantity are required.
                  </p>
                )}
                {status === "fallback" && (
                  <p style={{ fontFamily: futura, color: "#b71c1c", fontWeight: 600, fontSize: "14px", margin: "12px 0 0" }}>
                    Something hiccuped on our end.{" "}
                    <a href={mailtoHref()} style={{ color: "#ff7d1a" }}>
                      Click here to send your request by email instead
                    </a>
                    , everything you typed is already in it.
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Terms */}
      <section style={{ background: "#f7f7f7", padding: "60px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, fontSize: "clamp(20px, 2.2vw, 26px)" }}>
            Custom-order terms
          </h2>
          <ul style={{ margin: 0, padding: "0 0 0 20px" }}>
            {TERMS.map((t) => (
              <li key={t} style={{ ...bodyText, fontSize: "14.5px", marginBottom: "8px" }}>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Bulk order questions, answered
          </h2>
          {BULK_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: "15px", color: "#111" }}>
                {item.q}
              </summary>
              <p style={{ margin: "10px 0 0", fontSize: "15px", lineHeight: 1.55, color: "#555" }}>
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BulkOrders;
